import React from 'react'
import { useStaticQuery, graphql } from 'gatsby'
import styled from '@emotion/styled'
import Link from 'src/components/Link'
import Grid from 'src/components/Grid'
import Logo from 'src/components/Logo'
import SanityRichText from 'src/components/SanityRichText'
import { mq, colors, typography, animations } from 'src/styles'
import { Card } from 'src/styles/globals'
import GlassdoorSvg from 'src/assets/images/icon-glassdoor.svg'
import { FaTwitter, FaFacebookSquare, FaLinkedinIn, FaRss } from 'react-icons/fa'

const Wrapper = styled.div`
	position: relative;
	z-index: 2;
`

const FooterSection = styled.div`
	position: relative;
	z-index: 2;
	padding: var(--site-margins) 0 var(--site-margins) 0 !important;
	${ mq.smallAndBelow } {
		padding: var(--site-margins) 0 0 0 !important;
	}
`

const FooterCard = styled(Card)`
	padding: 56px 0;
	background: ${ colors.white };
	${ mq.smallAndBelow } {
		border-radius: 0;
		padding: 26px 0;
	}
`

const FooterLogo = styled(Logo)`
	width: 215px;
	* {
		fill: currentcolor;
	}
`

const FooterBottom = styled.div`
	position: relative;
	p {
		${ typography.bodyTiny }
		color: ${ colors.lightTextColor };
		line-height: 1.5em;
		margin: 0;
	}
`

const FooterColumns = styled.div`
	${ mq.mediumAndUp } {
		display: flex;
		align-items: flex-start;
		justify-content: space-between;
		flex-wrap: wrap;
	}
`

const SocialIcons = styled.ul`
	list-style: none;
	padding: 0;
	display: flex;
	align-items: center;
	justify-content: space-between;
	margin: 0 -12px 0 -10px;
	a {
		display: block;
		padding: 24px 10px;
		&:hover {
			color: ${ colors.mainColor };
		}
		svg {
			overflow: visible;
			width: 18px;
			height: 20px;
			display: block;
			* {
				fill: currentcolor;
			}
		}
	}
`

const Column = styled.div`
	margin-bottom: 44px;
	${ mq.mediumAndBelow } {
		width: 50%;
	}
	${ mq.smallAndBelow } {
		width: 100%;
		margin-bottom: 30px;
		${ ({ type }) => type === 'mobileMerge' ? `
			margin-bottom: 0;
		` : '' }
	}
	${ ({ type }) => type === 'social' ? `
		width: 100%;
		margin-bottom: 20px;
		${ mq.mediumAndBelow } {
			width: 100%;
		}
		${ mq.smallAndBelow } {
			margin-bottom: 8px;
		}
		${ mq.extraLargeAndUp } {
			width: auto;
		}
	` : '' }
	p {
		color: ${ colors.lightTextColor };
		margin: 0;
		${ typography.bodySmall }
		strong {
			color: ${ colors.textColor };
		}
		a {
			position: relative;
			display: inline-block;
			border: none;
			padding: 0;
			color: inherit;
			&:after {
				content: '';
				display: block;
				position: absolute;
				width: 5px;
				height: 5px;
				top: 50%;
				margin-top: -3px;
				border-bottom: 1px solid currentcolor;
				border-right: 1px solid currentcolor;
				transform: rotate(-45deg);
				left: 100%;
				opacity: 0;
				transition: transform ${ animations.mediumSpeed } ease-in-out, opacity ${ animations.mediumSpeed } ease-in-out;
			}
			&:hover {
				color: ${ colors.mainColor };
				&:after {
					opacity: 1;
					transform: rotate(-45deg) translate(4px, 4px);
				}
			}
		}
	}
`

const iconMap = {
	twitter: <FaTwitter/>,
	facebook: <FaFacebookSquare/>,
	glassdoor: <GlassdoorSvg/>,
	linkedin: <FaLinkedinIn/>,
	rss: <FaRss/>
}

const Footer = ({ legalText }) => {
	const {
		allSanitySiteSettings
	} = useStaticQuery(
		graphql`
			query {
				allSanitySiteSettings {
					edges {
						node {
							social {
			          socialLinks {
			            icon
			            url
			            _key
			          }
			        }
							footer {
								_rawColumnFourText(resolveReferences: {maxDepth: 10})
					      _rawColumnOneText(resolveReferences: {maxDepth: 10})
					      _rawColumnThreeText(resolveReferences: {maxDepth: 10})
					      _rawColumnTwoText(resolveReferences: {maxDepth: 10})
								footerLegalText
							}
						}
					}
				}
			}
		`
	)

	const siteSettings = allSanitySiteSettings.edges[0].node
	const footerContent = siteSettings?.footer
	const {
		_rawColumnFourText,
		_rawColumnOneText,
		_rawColumnThreeText,
		_rawColumnTwoText,
		footerLegalText
	} = footerContent

	return (
		<Wrapper setTheme="default">
			<FooterSection>
				<Grid small='[1]' medium='container'>
					<FooterCard>
						<Grid small='container' medium='1 [12] 1'>
							<FooterColumns small='[1] [1] [1] [1] [1]'>
								<Column type='social'>
									<div style={{ display: 'inline-block' }}>
										<FooterLogo />
										<SocialIcons>
											{siteSettings?.social?.socialLinks.map((item, index) => (
												<li key={'footer-social-link' + item.icon}><Link title={item.icon} to={item.url} external target='_blank'>{iconMap[item.icon]}</Link></li>
											))}
										</SocialIcons>
									</div>
								</Column>
								<Column>
									<SanityRichText text={_rawColumnOneText}/>
								</Column>
								<Column>
									<SanityRichText text={_rawColumnTwoText}/>
								</Column>
								<Column type='mobileMerge'>
									<SanityRichText text={_rawColumnThreeText}/>
								</Column>
								<Column>
									<SanityRichText text={_rawColumnFourText}/>
								</Column>
							</FooterColumns>
							{footerLegalText && (
								<FooterBottom>
									{legalText && legalText?.length > 0 ? (
										<SanityRichText text={legalText}/>
									) : (
										<p>{footerLegalText}</p>
									)}
								</FooterBottom>
							)}
						</Grid>
					</FooterCard>
				</Grid>
			</FooterSection>
		</Wrapper>
	)
}

export default Footer
